// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import validateRequiredDropdown from "@store/Framework/Common/Utilities/validateRequiredDropdown";
import useGreifLocations from "@store/Framework/Store/Greif/Components/LocationFilters/useGreifLocations";
import { BaseFormFieldProps, FormFieldValue } from "@store/Framework/Types/FormField";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC, useEffect, useState } from "react";

export const plantFormFieldLabelKey = "Greif Location";

type FieldValueType = string;
export interface PlantFormFieldValue extends FormFieldValue<FieldValueType> {
    displayValue?: string;
}

export interface PlantFormFieldPresentationProps extends ExpandStyles<PlantFormFieldStyles> {}

export interface PlantFormFieldProps extends PlantFormFieldPresentationProps, BaseFormFieldProps<FieldValueType> {
    disabled?: boolean;
    label?: React.ReactNode;
    required?: boolean;
}

export interface PlantFormFieldStyles {
    dynamicDropdown?: DynamicDropdownPresentationProps;
}

export const plantFormFieldStyles: PlantFormFieldStyles = {};

const PlantFormField: FC<PlantFormFieldProps> = ({
    defaultValue = "",
    disabled,
    extendedStyles,
    label,
    onUpdate,
    onValidate,
    overridedStyles,
    required,
    showError = true,
}) => {
    const styles = useExpandProps(plantFormFieldStyles, extendedStyles, overridedStyles);
    const { locations: plants, isLoading } = useGreifLocations();
    const [value, setValue] = useState(defaultValue);
    const [error, setError] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const textLabel = typeof label === "string" ? label : translate(`${plantFormFieldLabelKey}`);
    const getDisplayValue = (selectedValue: string) => {
        if (!selectedValue) {
            return "";
        }

        const selectedLocation = plants.find(plant => plant.location === selectedValue);

        return selectedLocation?.locationDescription || selectedLocation?.location || selectedValue;
    };

    const validate = (value: string, displayValue?: string) => {
        let errorMessage: string | undefined;

        if (required) {
            errorMessage = validateRequiredDropdown(value, textLabel);
        }

        const formFieldValue: PlantFormFieldValue = { isValid: !errorMessage, value, displayValue };
        errorMessage = onValidate?.(formFieldValue) ?? errorMessage;

        setError(errorMessage || "");

        return !errorMessage;
    };

    useEffect(() => {
        const _options: OptionObject[] = plants.map(plant => ({
            optionText: plant.locationDescription || plant.location,
            optionValue: plant.location,
        }));
        setOptions(_options);
    }, [plants]);

    useEffect(() => {
        const displayValue = getDisplayValue(value);
        const isValid = validate(value, displayValue);
        const formFieldValue: PlantFormFieldValue = { isValid, value, displayValue };
        onUpdate?.(formFieldValue);
    }, [plants]);

    const handleSelectionChange = (newValue?: string) => {
        const selectedValue = newValue ?? "";
        const displayValue = getDisplayValue(selectedValue);
        setValue(selectedValue);
        const isValid = validate(selectedValue, displayValue);
        const formFieldValue: PlantFormFieldValue = { isValid, value: selectedValue, displayValue };
        onUpdate?.(formFieldValue);
    };

    return (
        <DynamicDropdown
            {...styles.dynamicDropdown}
            disabled={disabled}
            error={showError ? error : ""}
            isLoading={isLoading}
            label={label || textLabel}
            onSelectionChange={handleSelectionChange}
            options={options}
            placeholder={translate("Select Location")}
            required={required}
            selected={value}
        />
    );
};

export default PlantFormField;
